import { getFeatures } from "@/utils/features";

import { impressionConfig } from "./config";
import { forceCloseOnUnmount, handleVisibilitySample } from "./episode";
import { onScrollSettled, startScrollTracking } from "./scroll";

import type { TrackContext } from "./types";

// One shared observer for every tracked status; its callbacks are the activity stream.
let observer: IntersectionObserver | null = null;
const registry = new Map<Element, TrackContext>(); // DOM node -> context

/** Same ≥50%-area OR ≥50%-viewport rule as the observer, computed from the live layout. */
const isVisibleNow = (element: Element): boolean => {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  if (viewportHeight <= 0) return false;
  const visible =
    Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
  const areaRatio = rect.height > 0 ? visible / rect.height : 0;
  const viewportFill = visible / viewportHeight;
  return (
    areaRatio >= impressionConfig.visibilityThreshold ||
    viewportFill >= impressionConfig.visibilityThreshold
  );
};

// Post-scroll sweep: once scrolling settles, re-sample everything still on screen as a non-fling
// read, so posts the user stopped on clear the fling gate even though no new observer sample fired.
const sweepVisibleItems = (): void => {
  const now = Date.now();
  for (const [element, ctx] of registry) {
    if (isVisibleNow(element)) handleVisibilitySample(ctx, true, now, element);
  }
};

const handleEntries: IntersectionObserverCallback = (entries) => {
  const now = Date.now();
  for (const entry of entries) {
    const ctx = registry.get(entry.target);
    if (!ctx) continue;

    const viewportHeight = entry.rootBounds?.height ?? window.innerHeight;
    const viewportFill =
      viewportHeight > 0 ? entry.intersectionRect.height / viewportHeight : 0;

    // Visible at ≥50% of the item's area OR when the item fills ≥50% of the viewport.
    const isVisible =
      entry.intersectionRatio >= impressionConfig.visibilityThreshold ||
      viewportFill >= impressionConfig.visibilityThreshold;

    handleVisibilitySample(ctx, isVisible, now, entry.target);
  }
};

const getObserver = (): IntersectionObserver => {
  if (!observer) {
    observer = new IntersectionObserver(handleEntries, {
      threshold: impressionConfig.thresholds,
    });
    startScrollTracking();
    onScrollSettled(sweepVisibleItems);
  }
  return observer;
};

const registerImpressionElement = (
  element: Element,
  ctx: TrackContext,
): void => {
  const features = getFeatures();
  if (!features.impressions) return;

  registry.set(element, ctx);
  getObserver().observe(element);
};

const unregisterImpressionElement = (
  element: Element,
  itemId: string,
): void => {
  getObserver().unobserve(element);
  registry.delete(element);
  forceCloseOnUnmount(itemId);
};

export { registerImpressionElement, unregisterImpressionElement };
